import { useEffect, useMemo, useState } from 'react';

import { CLIENTS_DEFAULT_SORT, CLIENTS_FETCH_PAGE_SIZE, CLIENTS_PAGE_SIZE } from '../../constants/clients';
import type { IClient, IClientsSort } from '../../interfaces/clients';
import type { IProspect } from '../../interfaces/prospects';
import ClientsService from '../../services/api/clients/clients';
import ProspectsService from '../../services/api/prospects/prospects';
import { ClientsStatus, type ClientsSortKey } from '../../types/clients';
import { ProspectStatus } from '../../types/prospects';
import {
  filterClientsByQuery,
  getSafePage,
  getTotalPages,
  mapClientApiToClients,
  mapProspectsToClients,
  paginateClients,
  sortClients,
  toggleSort
} from '../../utils/clients/clients';
import { useUser } from '../useUser/useUser';

interface IUseClientsParams {
  initialStatus?: ClientsStatus;
  pageSize?: number;
}

interface IClientsList {
  clients: IClient[];
  total: number;
}

interface IStatusTotals {
  active: number | null;
  awaitingStart: number | null;
}

const EMPTY_LIST: IClientsList = { clients: [], total: 0 };

const EMPTY_TOTALS: IStatusTotals = { active: null, awaitingStart: null };

const loadActiveClients = async (brokerId: string | null): Promise<IClientsList> => {
  if (!brokerId) return EMPTY_LIST;
  const { clients, pagination } = await ClientsService.getClients(brokerId, { pageSize: CLIENTS_FETCH_PAGE_SIZE });
  return { clients: mapClientApiToClients(clients), total: pagination.total };
};

const loadAwaitingStartClients = async (brokerId: string | null): Promise<IClientsList> => {
  if (!brokerId) return EMPTY_LIST;

  const [awaiting, pendingActivation] = await Promise.all([
    ProspectsService.getProspects(brokerId, { status: ClientsStatus.AwaitingStart }),
    ProspectsService.getProspects(brokerId, { status: ProspectStatus.PendingActivation }).catch(() => [] as IProspect[])
  ]);

  const erroredPending = pendingActivation.filter((prospect) => prospect.error);
  const byId = new Map<number, IProspect>();
  for (const prospect of [...erroredPending, ...awaiting]) byId.set(prospect.id, prospect);

  const clients = mapProspectsToClients([...byId.values()]);
  return { clients, total: clients.length };
};

export const useClients = ({
  initialStatus = ClientsStatus.Active,
  pageSize = CLIENTS_PAGE_SIZE
}: IUseClientsParams = {}) => {
  const { brokerId, loading: userLoading } = useUser();
  const [status, setStatus] = useState<ClientsStatus>(initialStatus);
  const [clients, setClients] = useState<IClient[]>([]);
  const [total, setTotal] = useState(0);
  const [activationErrorClients, setActivationErrorClients] = useState<IClient[]>([]);

  const [statusTotals, setStatusTotals] = useState<IStatusTotals>(EMPTY_TOTALS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<IClientsSort>(CLIENTS_DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);
  const [trackedBrokerId, setTrackedBrokerId] = useState(brokerId);

  if (brokerId !== trackedBrokerId) {
    setTrackedBrokerId(brokerId);
    setStatus(ClientsStatus.Active);
    setClients([]);
    setTotal(0);
    setActivationErrorClients([]);
    setStatusTotals(EMPTY_TOTALS);
    setError(false);
    setLoading(true);
    setQuery('');
    setSort(CLIENTS_DEFAULT_SORT);
    setPage(1);
  }

  useEffect(() => {
    if (userLoading) return;

    let cancelled = false;

    void Promise.all([
      loadActiveClients(brokerId).catch(() => null),
      loadAwaitingStartClients(brokerId).catch(() => null)
    ])
      .then(([active, awaitingStart]) => {
        if (cancelled) return;
        const list = status === ClientsStatus.AwaitingStart ? awaitingStart : active;

        if (!list) {
          setError(true);
          return;
        }

        setClients(list.clients);
        setTotal(list.total);
        setStatusTotals({ active: active?.total ?? null, awaitingStart: awaitingStart?.total ?? null });
        setActivationErrorClients((awaitingStart?.clients ?? []).filter((client) => client.hasActivationError));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [status, reloadToken, brokerId, userLoading]);

  const visible = useMemo(() => sortClients(filterClientsByQuery(clients, query), sort), [clients, query, sort]);

  const totalPages = getTotalPages(visible.length, pageSize);
  const safePage = getSafePage(page, totalPages);
  const pageClients = useMemo(() => paginateClients(visible, safePage, pageSize), [visible, safePage, pageSize]);

  const startLoad = () => {
    setClients([]);
    setTotal(0);
    setActivationErrorClients([]);
    setError(false);
    setLoading(true);
  };

  const handleStatusChange = (next: ClientsStatus) => {
    if (next === status) return;
    startLoad();
    setStatus(next);
    setSort(CLIENTS_DEFAULT_SORT);
    setQuery('');
    setPage(1);
  };

  const handleQueryChange = (next: string) => {
    setQuery(next);
    setPage(1);
  };

  const handleSortChange = (key: ClientsSortKey) => {
    setSort((current) => toggleSort(current, key));
    setPage(1);
  };

  const retry = () => {
    startLoad();
    setReloadToken((token) => token + 1);
  };

  const isFirstUse = !loading && !error && statusTotals.active === 0 && statusTotals.awaitingStart === 0;

  return {
    status,
    onStatusChange: handleStatusChange,
    clients: pageClients,
    activationErrorClients,
    total,
    filteredTotal: visible.length,
    loading,
    error,
    query,
    onQueryChange: handleQueryChange,
    sort,
    onSortChange: handleSortChange,
    page: safePage,
    pageSize,
    onPageChange: setPage,
    retry,
    isFirstUse
  };
};
